import React, { useEffect, useState, useRef } from 'react';
import { format } from 'date-fns';
import { transactionApi } from '../services/api';
import '../pages/TransactionPage.css';
import { toast } from 'sonner'; // You must run: npm install sonner


export default function FrontOfficeTransactionBlotter() {
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectMsg, setRejectMsg] = useState('');
  const [rejectTx, setRejectTx] = useState(null);
  const user = JSON.parse(localStorage.getItem('user'));

  // For polling
  const prevRejectedDealsRef = useRef([]);
  useEffect(() => {
    fetchTransactions();
    const interval = setInterval(fetchTransactions, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, []);

  function fetchTransactions() {
    setIsLoading(true);
    setError('');
    transactionApi.getAll()
      .then(allTx => {
        // Only show transactions pending at front office (case-insensitive, robust)
        const filtered = allTx.filter(tx => {
          const status = String(tx.approval_status || tx.status || '').toLowerCase();
          const approval = String(tx.current_approval_level || '').toLowerCase();
          return status === 'pending' && (approval === 'front_office' || !approval);
        });
        setTransactions(filtered);

        // --- Sonner notification for rejected deals ---
        const rejectedDeals = allTx.filter(tx => {
          // Customize this logic if your backend uses another field for rejection
          return (
            String(tx.status).toLowerCase() === 'rejected' &&
            (String(tx.current_approval_level || '').toLowerCase() === 'front_office')
          );
        });
        // Only notify for new rejected deals
        const prevRejectedDeals = prevRejectedDealsRef.current;
        const newRejectedDeals = rejectedDeals.filter(tx =>
          !prevRejectedDeals.some(prev => prev.deal_number === tx.deal_number)
        );
        if (newRejectedDeals.length > 0) {
          newRejectedDeals.forEach(tx => {
            toast.error(`Deal ${tx.deal_number} was rejected by Back Office: ${tx.comment || 'No comment provided.'}`);
          });
        }
        prevRejectedDealsRef.current = rejectedDeals;
      })
      .catch(err => {
        setError('Failed to load transactions.');
        setTransactions([]);
      })
      .finally(() => setIsLoading(false));
  }

  function handleEscalate(dealNumber) {
    if (!dealNumber) {
      setError('Cannot escalate: deal number is missing.');
      return;
    }
    // Escalate by updating status and approval level, using deal_number as identifier
    transactionApi.update(
      dealNumber,
      { approval_status: 'APPROVED', current_approval_level: 'back_office' },
      { headers: { 'x-user-data': JSON.stringify(user) } }
    )
      .then(() => {
        fetchTransactions();
      })
      .catch(err => {
        setError(err?.response?.data?.message || err.message || 'Failed to escalate transaction.');
      });
  }

  function handleReject() {
    if (!rejectMsg.trim()) return;
    transactionApi.authorizeTransaction({ transactionId: rejectTx.id, action: 'REJECT', comment: rejectMsg })
      .then(() => {
        setRejectModalOpen(false);
        setRejectMsg('');
        setRejectTx(null);
        fetchTransactions();
      });
  }

  return (
    <div className="page-container sister-blotter">
      <h2>Front Office Transaction Blotter</h2>
      {isLoading ? (
        <div className="no-transactions-message">Loading...</div>
      ) : error ? (
        <div className="no-transactions-message">{error}</div>
      ) : transactions.length === 0 ? (
        <div className="no-transactions-message">No pending transactions for front office approval.</div>
      ) : (
        <div className="transactions-table-wrapper">
          <table className="transactions-table">
            <thead>
              <tr>
                <th>Deal Number</th>
                <th>Trade Date</th>
                <th>Price</th>
                <th>Portfolio</th>
                <th>Security</th>
                <th>Interest Rate</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx, index) => {
                // Highlight if this deal was rejected by back office and returned
                const isRejected = String(tx.status).toLowerCase() === 'rejected';
                return (
                  <tr key={tx.deal_number || index} className={isRejected ? 'rejected-row' : ''}>
                    <td>
                      {tx.deal_number || 'N/A'}
                      {isRejected && (
                        <span className="status-badge status-rejected" style={{marginLeft:6}}>Rejected</span>
                      )}
                    </td>
                    <td>{tx.tradeDate ? format(new Date(tx.tradeDate), 'yyyy-MM-dd') : 'N/A'}</td>
                    <td className="numeric-cell">{tx.price ? Number(tx.price).toFixed(4) : 'N/A'}</td>
                    <td>{tx.portfolio || 'N/A'}</td>
                    <td>{tx.security_name || tx.security || 'N/A'}</td>
                    <td className="numeric-cell">{tx.interestRate || tx.interest_rate ? Number(tx.interestRate || tx.interest_rate).toFixed(4) : 'N/A'}</td>
                    <td>
                      <button className="view-details-btn" style={{marginRight:8}} onClick={() => { setSelectedTransaction(tx); setShowDetails(true); }}>View</button>
                      <button className="approve-btn" style={{marginRight:8}} onClick={() => handleEscalate(tx.deal_number)}>Escalate to Back Office</button>
                      <button className="reject-btn" onClick={() => { setRejectTx(tx); setRejectModalOpen(true); }}>Reject</button>
                      {isRejected && tx.comment && (
                        <div style={{color:'#ef4444', fontSize:'0.85em', marginTop:4}}>
                          Reason: {tx.comment}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

      {/* Transaction Details Modal */}
      {showDetails && selectedTransaction && (
        <div className="modal-overlay" onClick={() => setShowDetails(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Transaction Details</h3>
            <div style={{marginBottom: '1em'}}>
              <div><b>Deal Number:</b> {selectedTransaction.deal_number || 'N/A'}</div>
              <div><b>Trade Date:</b> {selectedTransaction.tradeDate ? format(new Date(selectedTransaction.tradeDate), 'yyyy-MM-dd') : 'N/A'}</div>
              <div><b>Price:</b> {selectedTransaction.price ? Number(selectedTransaction.price).toFixed(4) : 'N/A'}</div>
              <div><b>Portfolio:</b> {selectedTransaction.portfolio || 'N/A'}</div>
              <div><b>Security:</b> {selectedTransaction.security_name || selectedTransaction.security || 'N/A'}</div>
              <div><b>Interest Rate:</b> {selectedTransaction.interestRate || selectedTransaction.interest_rate ? Number(selectedTransaction.interestRate || selectedTransaction.interest_rate).toFixed(4) : 'N/A'}</div>
              <div><b>Amount:</b> {selectedTransaction.amount ? Number(selectedTransaction.amount).toFixed(4) : 'N/A'}</div>
              <div><b>Status:</b> {selectedTransaction.status || 'Pending'}</div>
              <div><b>Value Date:</b> {selectedTransaction.valueDate ? format(new Date(selectedTransaction.valueDate), 'yyyy-MM-dd') : 'N/A'}</div>
              <div><b>Remarks:</b> {selectedTransaction.remarks || 'N/A'}</div>
              <div><b>Description:</b> {selectedTransaction.description || 'N/A'}</div>
            </div>
            <button onClick={() => setShowDetails(false)}>Close</button>
          </div>
        </div>
      )}

        </div>
      )}

      {/* Reject Modal */}
      {rejectModalOpen && (
        <div className="modal-overlay" onClick={() => setRejectModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Reject Transaction</h3>
            <textarea
              rows={4}
              style={{width: '100%', marginBottom: 16}}
              placeholder="Enter rejection reason/message"
              value={rejectMsg}
              onChange={e => setRejectMsg(e.target.value)}
            />
            <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
              <button onClick={() => setRejectModalOpen(false)}>Cancel</button>
              <button className="reject-btn" style={{background:'#c62828',color:'#fff'}} onClick={handleReject}>Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
